/**
 * BadgeWall — the full achievement grid on a user's public page. Renders EVERY
 * badge from the generated manifest: earned ones in full color, unearned ones
 * desaturated with a lock — the wall doubles as the "what can I earn" map.
 * The earned set comes from {@link useUserBadges} (the live `user_badges`
 * subscription). When the viewer is the wall's OWNER, `progress` carries their
 * `my_badge_progress` metric values and every unearned tile shows how close
 * they are (`min(value, threshold)/threshold`, thresholds from the generated
 * BADGE_DEFS — same manifest, same regen). Visitors get no progress.
 */
import { BADGE_DEFS, type BadgeMetric, type UserBadge } from '@djgg/creations';
import { BADGES } from './badgeManifest.js';

export interface BadgeWallProps {
  /** Complete earned rows keyed by badge id. */
  earned: ReadonlyMap<string, UserBadge & { id: string }>;
  /** Owners see the complete collection; visitors see earned badges only. */
  owner: boolean;
  /** Owner-only metric values keyed by badge metric; absent for visitors. */
  progress?: ReadonlyMap<BadgeMetric, number>;
}

export function BadgeWall({ earned, owner, progress }: BadgeWallProps) {
  const badges = [...BADGES]
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id))
    .filter((badge) => owner || earned.has(badge.id));
  if (badges.length === 0) {
    return <div className="empty badge-wall__empty">No badges earned yet.</div>;
  }
  return (
    <div className="badge-wall" data-testid="badge-wall">
      {badges.map((badge) => {
        const isEarned = earned.has(badge.id);
        const award = earned.get(badge.id);
        // Art manifest and machine defs are generated from the ONE badges
        // manifest, so the id lookup always resolves.
        const def = BADGE_DEFS.find((d) => d.id === badge.id);
        const value = def && progress ? Math.min(progress.get(def.metric) ?? 0, def.threshold) : 0;
        return (
          <div
            key={badge.id}
            className={`badge-tile${isEarned ? '' : ' badge-tile--locked'}`}
            data-testid="badge-wall-item"
            data-badge-id={badge.id}
            data-earned={isEarned}
          >
            <div className="badge-tile__art">
              <img
                className="badge-tile__img"
                src={badge.src}
                alt={badge.alt}
                loading="lazy"
                style={{
                  transform: `translate(${badge.displayOffsetX}%, ${badge.displayOffsetY}%) scale(${badge.displayScale})`,
                }}
              />
            </div>
            {!isEarned && (
              <svg
                className="badge-tile__lock"
                viewBox="0 0 24 24"
                width={14}
                height={14}
                role="img"
                aria-label="Locked"
              >
                <path d="M7 10V7a5 5 0 0 1 10 0v3h1a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h1zm2 0h6V7a3 3 0 0 0-6 0v3z" />
              </svg>
            )}
            <div className="badge-tile__name">{badge.name}</div>
            <div className="badge-tile__criteria">{badge.criteria}</div>
            {award && (
              <div className="badge-tile__date" data-testid="badge-awarded-date">
                Received {new Date(award.awardedAt).toLocaleDateString()}
              </div>
            )}
            {owner && !isEarned && def && progress && (
              <div className="badge-tile__progress" data-testid="badge-progress">
                <div className="badge-tile__progress-track">
                  <div
                    className="badge-tile__progress-fill"
                    style={{ width: `${(value / def.threshold) * 100}%` }}
                  />
                </div>
                <div className="badge-tile__progress-text">
                  {value}/{def.threshold}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
